<template>
  <div>
    <div class="category-block">
      <div class="text-header">
        <div class="section-title text-title">■ ADD TEXT</div>
        <button class="add-text-btn" @click="openTextModal">Add / edit text</button>
      </div>
            <div v-if="localTextItems && localTextItems.length > 0" class="text-gallery-grid">
              <div
                v-for="item in localTextItems"
                :key="item.id + '-' + currentGarment"
                class="text-gallery-item"
                :class="{ 'editing': currentEditingLogo && currentEditingLogo.isText && currentEditingLogo.id === item.id }"
                @click="openEditTextModal(item)"
                :title="item.text + ' (' + item.part + ')'"
              >
                <div 
                  class="text-thumb-content"
                  :style="{ 
                    color: item.color || '#000', 
                    fontFamily: item.font || 'Arial',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    wordBreak: 'break-all',
                    padding: '4px'
                  }"
                >
                  {{ (item.text || '').substring(0, 5) }}{{ (item.text || '').length > 5 ? '...' : '' }}
                </div>
                
                <button 
                  class="icon-btn edit-text-btn" 
                  :data-id="item.id" 
                  :data-part="item.part" 
                  @click.stop="openEditTextModal(item)"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17 3l4 4L7 21H3v-4L17 3z"/>
                    <path d="M15 5l4 4"/>
                  </svg>
                </button>
              </div>
            </div>
            <div v-else class="text-empty-state">
              Add text to the DESIGN AREA
            </div>
        
    </div>

    <!-- 文字编辑弹窗 -->
    <div class="text-modal-overlay" ref="textModalOverlay">
      <div class="text-modal">
        <button class="close-modal-btn" @click="closeModal">✕</button>
        <h3>Text Editor</h3>
        <div class="modal-subtitle"></div>

        <input type="text" ref="textModalInput" placeholder="name, slogan, etc" autocomplete="off" class="modal-text-input">



        <div class="modal-section">
          <div class="modal-label">Font Style</div>
          <div class="modal-font-grid">
            <div 
              v-for="font in fonts" 
              :key="font.value"
              :class="['modal-font-card', { 'selected': selectedFont === font.value }]"
              @click="selectFont(font.value)"
            >
              <span :style="{ fontFamily: font.value, fontSize: '0.7rem', fontWeight: '500', color: 'white', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }">
                {{ font.name }}
              </span>
            </div>
          </div>
        </div>

        <div class="modal-section">
          <div class="modal-color-size-row">
            <div class="modal-label">Text Color</div>
            <div class="modal-label">Font Size</div>
          </div>
          <div style="display: flex; gap: 12px; align-items: center;">
            <label class="color-picker-label" style="width: 80px; height: 44px; border-radius: 16px; background: linear-gradient(90deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff); border: 2px solid #475569; cursor: pointer; flex-shrink: 0; display: inline-block;"></label>
            <input type="color" ref="realColorPicker" style="position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none;">
            <input type="text" ref="modalColorInput" placeholder="#000000" value="#000000" autocomplete="off" style="flex: 0.5; padding: 10px 12px; background: #334155; border: 1px solid #475569; border-radius: 16px; color: white; font-family: monospace; font-size: 0.9rem;">
            <select ref="modalFontSizeSelect" style="flex: 0.5; padding: 10px 8px; background: #334155; border: 1px solid #475569; border-radius: 16px; color: white; font-size: 0.9rem; cursor: pointer;">
              <option value="100">100px</option>
              <option value="150">150px</option>
              <option value="200">200px</option>
              <option value="250">250px</option>
              <option value="300">300px</option>
              <option value="340">340px</option>
              <option value="360" selected>360px</option>
              <option value="380">380px</option>
              <option value="400">400px</option>
              <option value="450">450px</option>
            </select>
          </div>
        </div>

        <div class="text-modal-buttons">
          <button class="text-modal-btn cancel" @click="closeModal">Cancel</button>
          <button class="text-modal-btn confirm" @click="confirmText">Apply</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useDesignStore } from '@/stores/designStore'

export default {
  name: 'TextEditor',
  setup() {
    // 在 setup 中获取 store 实例并返回，这样模板可以直接使用
    const store = useDesignStore()
    return { store }
  },
  data() {
    return {
      editingTextItem: null,
      selectedFont: 'Arial',
      localTextItems: [], // 本地缓存，用于强制响应式更新
      availableParts: ['front', 'back', 'collar', 'left sleeve', 'right sleeve', 'left side', 'right side', 'pocket', 'hood'],
      fonts: [
        { name: 'Algerian', value: 'Algerian' },
        { name: 'Arial', value: 'Arial' },
        { name: 'Arial Black', value: 'Arial Black' },
        { name: 'Bebas Neue', value: 'Bebas Neue' },
        { name: 'Bernard MT Condensed', value: 'Bernard MT Condensed' },
        { name: 'Big Shoulders Display', value: 'Big Shoulders Display' },
        { name: 'Blackadder ITC', value: 'Blackadder ITC' },
        { name: 'Broadway', value: 'Broadway' },
        { name: 'Calisto MT', value: 'Calisto MT' },
        { name: 'Comic Sans MS', value: 'Comic Sans MS' },
        { name: 'Cooper Black', value: 'Cooper Black' },
        { name: 'Courgette', value: 'Courgette' },
        { name: 'Courier New', value: 'Courier New' },
        { name: 'Creepster', value: 'Creepster' },
        { name: 'Franklin Gothic Heavy', value: 'Franklin Gothic Heavy' },
        { name: 'Frijole', value: 'Frijole' },
        { name: 'Georgia', value: 'Georgia' },
        { name: 'Helvetica', value: 'Helvetica' },
        { name: 'Impact', value: 'Impact' },
        { name: 'Lucida Calligraphy', value: 'Lucida Calligraphy' },
        { name: 'Montserrat', value: 'Montserrat' },
        { name: 'Old English Text MT', value: 'Old English Text MT' },
        { name: 'Orbitron', value: 'Orbitron' },
        { name: 'Oswald-Bold', value: 'Oswald-Bold' },
        { name: 'Pacifico', value: 'Pacifico' },
        { name: 'Playbill', value: 'Playbill' },
        { name: 'Poppins', value: 'Poppins' },
        { name: 'RA BALI', value: 'RA BALI' },
        { name: 'Righteous', value: 'Righteous' },
        { name: 'Stencil', value: 'Stencil' },
        { name: 'Times New Roman', value: 'Times New Roman' },
        { name: 'Trebuchet MS', value: 'Trebuchet MS' },
        { name: 'UnifrakturCook', value: 'UnifrakturCook' },
        { name: 'Verdana', value: 'Verdana' },
        { name: 'Wallpoet', value: 'Wallpoet' },
        { name: 'Zero G', value: 'Zero G' }
      ]
    }
  },
  computed: {
    // 用 mapState 确保 Vue 响应式追踪正确
    ...mapState(useDesignStore, ['textItems', 'currentSelectedPart', 'currentEditingLogo', 'currentGarment']),
    // 计算属性：直接返回本地缓存的 textItems
    displayTextItems() {
      return this.localTextItems
    }
  },
  watch: {
    textItems: {
      handler(newVal) {
        if (newVal && newVal.length) {
          this.localTextItems = newVal.map(item => ({ ...item }))
        } else {
          this.localTextItems = []
        }
        if (this._isMounted) {
          this.$nextTick(() => this.$forceUpdate())
        }
      },
      deep: true,
      immediate: true
    },
    currentGarment: {
      handler(newVal) {
        const items = this.store.textItems
        this.localTextItems = items && items.length ? items.map(i => ({ ...i })) : []
        if (this._isMounted) {
          this.$nextTick(() => this.$forceUpdate())
        }
      },
      immediate: true
    },
    'store.currentEditingLogo': {
      handler() {
        if (this._isMounted) this.$nextTick(() => this.$forceUpdate())
      },
      deep: true,
      immediate: true
    }
  },

  mounted() {
    this._isMounted = true
    this.setupColorPicker()
    // 立即同步一次
    this.syncFromStore()
    // 延迟再同步一次，覆盖 loadSharedDesign 异步完成后写入数据的情况
    setTimeout(() => this.syncFromStore(), 800)
    window.addEventListener('garment-switched', this.onGarmentSwitched)
  },

  beforeDestroy() {
    this._isMounted = false
    window.removeEventListener('garment-switched', this.onGarmentSwitched)
  },

  methods: {
    ...mapActions(useDesignStore, ['addTextToPart', 'updateLayerProperty', 'selectLogo', 'appendTextItem']),

    // 公开方法：强制从 store 同步 textItems，供外部（App.vue）在 loadSharedDesign 后调用
    syncFromStore() {
      const items = this.store.textItems
      this.localTextItems = items && items.length ? items.map(i => ({ ...i })) : []
      if (this._isMounted) this.$nextTick(() => this.$forceUpdate())
    },

    selectFont(fontValue) {
      this.selectedFont = fontValue
    },
    
  // 款式切换时的回调
    onGarmentSwitched(event) {
      console.log('[v0] TextEditor: garment-switched event received', event.detail)
      const newTextItems = event.detail.textItems
      if (newTextItems && newTextItems.length) {
        this.localTextItems = newTextItems.map(item => ({ ...item }))
      } else {
        this.localTextItems = []
      }
      this.$forceUpdate()
    },

    getTextItemImageSrc(item) {
      if (item.img && item.img.src) {
        return item.img.src
      }
      if (item.text && item.font && item.color) {
        const canvas = document.createElement('canvas')
        canvas.width = 128
        canvas.height = 128
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, 128, 128)
        let fs = 24
        ctx.font = `Bold ${fs}px "${item.font}", Arial, sans-serif`
        ctx.fillStyle = item.color
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(item.text.substring(0, 6), 64, 64)
        return canvas.toDataURL()
      }
      return null
    },

    setupColorPicker() {
      const colorPickerLabel = document.querySelector('.color-picker-label')
      const realColorPicker = this.$refs.realColorPicker
      const modalColorInput = this.$refs.modalColorInput
      if (realColorPicker && modalColorInput && colorPickerLabel) {
        realColorPicker.addEventListener('input', (e) => {
          modalColorInput.value = e.target.value
        })
        colorPickerLabel.addEventListener('click', () => {
          realColorPicker.click()
        })
      }
    },
    
    async createTextTexture(text, font, color, customFontSize = null) {
      await document.fonts.ready
      const fontCheck = `12px "${font}"`
      if (!document.fonts.check(fontCheck)) {
        try {
          await document.fonts.load(fontCheck)
        } catch (e) {
          console.warn(`Font load failed: ${font}`, e)
        }
      }
      const canvas = document.createElement('canvas')
      canvas.width = 512
      canvas.height = 512
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      let fontSize = customFontSize || 80
      ctx.font = `Bold ${fontSize}px "${font}", Arial, sans-serif`
      ctx.fillStyle = color
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      let textWidth = ctx.measureText(text).width
      const maxWidth = canvas.width - 40
      while (textWidth > maxWidth && fontSize > 12) {
        fontSize -= 5
        ctx.font = `Bold ${fontSize}px "${font}", Arial, sans-serif`
        textWidth = ctx.measureText(text).width
      }
      const singleLineText = text.replace(/[\r\n]+/g, ' ')
      ctx.font = `Bold ${fontSize}px "${font}", Arial, sans-serif`
      ctx.fillStyle = color
      ctx.fillText(singleLineText, canvas.width / 2, canvas.height / 2)
      return canvas
    },

    createThumbCanvas(text, font, color, fontSize) {
      const canvas = document.createElement('canvas')
      canvas.width = 128
      canvas.height = 128
      const ctx = canvas.getContext('2d')
      // 白色背景
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, 128, 128)
      const safeFont = `"${font}", Arial, "Helvetica Neue", sans-serif`
      let fs = 36
      ctx.font = `Bold ${fs}px ${safeFont}`
      // 文字颜色：太浅的颜色（接近白色）换成深色，确保在白底上可见
      const isLightColor = (c) => {
        const hex = c.replace('#', '')
        const r = parseInt(hex.substr(0,2),16)
        const g = parseInt(hex.substr(2,2),16)
        const b = parseInt(hex.substr(4,2),16)
        return (r*299 + g*587 + b*114) / 1000 > 200
      }
      ctx.fillStyle = color && !isLightColor(color) ? color : '#1e293b'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const cleanText = (text || 'T').replace(/[\r\n]+/g, ' ')
      const maxW = 118
      let tw = ctx.measureText(cleanText).width
      while (tw > maxW && fs > 8) {
        fs -= 3
        ctx.font = `Bold ${fs}px ${safeFont}`
        tw = ctx.measureText(cleanText).width
      }
      ctx.fillText(cleanText, 64, 64)
      return canvas.toDataURL()
    },
    
    openTextModal() {
      this.editingTextItem = null
      this.selectedFont = 'Arial'
      this.$refs.textModalInput.value = ''
      this.$refs.modalColorInput.value = '#000000'
      this.$refs.modalFontSizeSelect.value = '360'
      if (this.$refs.targetPartSelect) {
        this.$refs.targetPartSelect.value = this.currentSelectedPart || 'front'
      }
      this.$refs.textModalOverlay.style.display = 'flex'
      this.$nextTick(() => {
        this.$refs.textModalInput.focus()
      })
    },
    
    openEditTextModal(textItem) {
      this.editingTextItem = textItem
      this.selectedFont = textItem.font || 'Arial'
      this.$refs.textModalInput.value = textItem.text
      this.$refs.modalColorInput.value = textItem.color || '#000000'
      if (this.$refs.modalFontSizeSelect && textItem.fontSize) {
        this.$refs.modalFontSizeSelect.value = textItem.fontSize
      }
      if (this.$refs.targetPartSelect) {
        this.$refs.targetPartSelect.value = textItem.part || this.currentSelectedPart
      }
      this.$refs.textModalOverlay.style.display = 'flex'
      this.$refs.textModalInput.focus()
    },
    
    async confirmText() {
      console.log('🔥 confirmText 开始执行')
      
      let newText = this.$refs.textModalInput.value.trim().replace(/[\r\n]+/g, ' ') || 'Your Text'
      let selectedFont = this.selectedFont
      let newColor = this.$refs.modalColorInput.value.trim()
      if (!/^#[0-9A-Fa-f]{6}$/.test(newColor)) newColor = '#000000'
      let fontSize = parseInt(this.$refs.modalFontSizeSelect.value) || 80
      if (isNaN(fontSize) || fontSize < 20) fontSize = 80

      const store = this.store

      let part
      if (this.editingTextItem) {
        part = this.editingTextItem.part
      } else {
        part = store.currentSelectedPart || 'front'
      }
      
      console.log('目标部位:', part)
      console.log('当前 store.textItems 长度:', store.textItems.length)

      const textItemId = this.editingTextItem ? this.editingTextItem.id : null

      const textCanvas = await this.createTextTexture(newText, selectedFont, newColor, fontSize)
      const imgSrc = textCanvas.toDataURL()
      const img = await new Promise((resolve) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.src = imgSrc
      })

      if (this.editingTextItem) {
        this.updateLayerProperty(part, textItemId, 'img', img)
        this.updateLayerProperty(part, textItemId, 'textContent', newText)
        this.updateLayerProperty(part, textItemId, 'textFont', selectedFont)
        this.updateLayerProperty(part, textItemId, 'textColor', newColor)
        this.updateLayerProperty(part, textItemId, 'fontSize', fontSize)
        const thumbSrc = this.createThumbCanvas(newText, selectedFont, newColor, fontSize)
        const idx = store.textItems.findIndex(t => t.id === textItemId)
        if (idx !== -1) {
          const updated = [...store.textItems]
          updated[idx] = { ...updated[idx], text: newText, font: selectedFont, color: newColor, fontSize: fontSize, img: img, imgSrc: thumbSrc }
          store.textItems = updated
          // 同步到本地数据
          this.localTextItems = [...updated]
        }
        this.selectLogo(part, textItemId, true)
        if (window.$viewer3D) {
          window.$viewer3D.compositePartTexture(part, textItemId)
          window.$viewer3D.showTextureControls()
        }
      } else {
        // 使用组件内部渲染的高清缩略图 Base64 地址
        const thumbSrc = this.createThumbCanvas(newText, selectedFont, newColor, fontSize)

        // 组装标准的传递对象，把生成的 img 和 imgSrc 传进去，让 store 完美保留
        const textParam = {
          text: newText,
          font: selectedFont,
          color: newColor,
          fontSize: fontSize,
          scale: 0.8,
          offsetX: 0,
          offsetY: 0,
          rotation: 0,
          img: img,
          imgSrc: thumbSrc
        }

        console.log('准备提交给 Store 的文字参数:', textParam)

        // 核心：调用映射自 pinia store 的核心方法，交由 store 去管理自增 ID 与冗余 textItems 列表
        await this.addTextToPart(part, textParam)

        // ★★★ 关键修复：强制刷新 textItems 列表，确保缩略图立即显示 ★★★
        await this.$nextTick()
        this.$forceUpdate()

        // 手动触发 store 的 textItems 更新通知
        if (store.textItems && store.textItems.length > 0) {
          // 强制重新赋值触发响应式更新
          store.textItems = [...store.textItems]
        }

        console.log('文字添加完成，当前 textItems 长度:', store.textItems.length)
        console.log('当前 textItems 内容:', store.textItems)
        
        // 同步到本地数据
        this.localTextItems = [...store.textItems]
      }

      this.closeModal()
      
      await this.$nextTick()
      this.$forceUpdate()
    },
    
    closeModal() {
      this.$refs.textModalOverlay.style.display = 'none'
    }
  }
}
</script>

<style scoped>

.text-gallery-grid {
  display: grid !important;
  grid-template-columns: repeat(10, 1fr);
  gap: 4px;
  max-height: 180px;
  overflow-y: auto;
  padding: 6px;
  background: transparent;
  border-radius: 8px;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  margin-top: 8px;
}

.text-gallery-item {
  aspect-ratio: 1 / 1;
  background: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.text-gallery-item:hover {
  transform: scale(1.05);
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.text-gallery-item.editing {
  background: #f0f0f0;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
}

.text-thumb-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 4px;
  pointer-events: none;
}

.text-empty-state {
  font-size: 0.7rem;
  color: #666;
  text-align: center;
  grid-column: 1 / -1;
  padding: 8px;
}

.text-thumb-fallback {
  font-size: 0.65rem;
  font-weight: bold;
  text-align: center;
  padding: 2px;
  overflow: hidden;
  max-width: 100%;
}
.icon-btn {
  background: #2d3748;
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.add-text-btn {
  background: #e0e7ff;
  border: 1px solid #c7d2fe;
  color: #3730a3;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
}
.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #072a47;
  margin-bottom: 0.3rem;
  padding-left: 4px;
}
.category-block {
  margin-bottom: 0.6rem;
}
.text-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 20000;
  display: none;
  justify-content: center;
  align-items: center;
}
.text-modal {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-radius: 24px;
  padding: 2.2rem 2rem 2rem;
  width: 90%;
  max-width: 600px;
  text-align: center;
  border: 1px solid rgba(59, 130, 246, 0.5);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  position: relative;
}
.text-modal h3 {
  color: #ffffff;
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  letter-spacing: -0.3px;
}
.text-modal .modal-subtitle {
  color: #94a3b8;
  font-size: 0.8rem;
  margin-bottom: 1.5rem;
}
.text-modal input,
.text-modal input[type="text"],
.text-modal input[type="email"],
.text-modal select {
  width: 100%;
  padding: 12px 16px;
  background: #334155;
  border: 1px solid #475569;
  border-radius: 16px;
  font-size: 1rem;
  color: #ffffff;
  outline: none;
  box-sizing: border-box;
  font-family: inherit;
}
.text-modal input:focus,
.text-modal select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}
.text-modal-buttons {
  display: flex;
  gap: 12px;
  margin-top: 1.5rem;
}
.text-modal-btn {
  flex: 1;
  padding: 10px 0;
  border-radius: 40px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  font-family: inherit;
}
.text-modal-btn.confirm {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}
.text-modal-btn.confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
}
.text-modal-btn.cancel {
  background: #334155;
  color: #cbd5e1;
}
.text-modal-btn.cancel:hover {
  background: #475569;
}
.close-modal-btn {
  position: absolute;
  top: 14px;
  right: 18px;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.3rem;
  cursor: pointer;
  transition: color 0.2s;
}
.close-modal-btn:hover {
  color: #ef4444;
}
.modal-font-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;
  margin-bottom: 16px;
}
.modal-font-card {
  background: #334155;
  border: 2px solid #475569;
  border-radius: 8px;
  padding: 8px 4px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}
.modal-font-card:hover {
  transform: scale(1.02);
  border-color: #60a5fa;
}
.modal-font-card.selected {
  border-color: #3b82f6;
  background: #1e40af;
}

.text-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.text-title {
  margin-bottom: 0;
}

.text-thumb-grid {
  margin-top: 8px;
}

.text-thumb-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 4px;
  pointer-events: none;
}

.text-empty-state {
  font-size: 0.7rem;
  color: #666;
  text-align: center;
  grid-column: 1 / -1;
  padding: 8px;
}

.text-modal-overlay {
  display: none;
}

.modal-text-input {
  margin-bottom: 16px;
}

.modal-section {
  margin-bottom: 16px;
}

.modal-label {
  color: #94a3b8;
  font-size: 0.75rem;
  margin-bottom: 8px;
}

.modal-select {
  width: 100%;
  padding: 10px 12px;
  background: #334155;
  border: 1px solid #475569;
  border-radius: 16px;
  color: white;
  font-size: 0.9rem;
}

.modal-color-size-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.text-thumb-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border-radius: 4px;
  overflow: hidden;
}

/* 编辑按钮样式 - 无背景圆圈，位于右上角 */
.edit-text-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  background: transparent !important;
  color: #3b82f6;
  width: 24px;
  height: 24px;
  border-radius: 0;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.edit-text-btn:hover {
  opacity: 1;
  background: transparent !important;
}

@media (max-width: 850px) {
  .layer-list {
    gap: 0.3rem;
    margin-top: 4px;
    grid-template-columns: repeat(3, 1fr);
  }
  .layer-item {
    padding: 0.25rem 0.4rem;
  }
  .modal-font-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 768px) {
  .text-modal {
    padding: 1.2rem 1.2rem 1.2rem;
  }
  .text-modal h3 {
    font-size: 1.1rem;
    margin-bottom: 0.15rem;
    margin-top: 0;
    line-height: 1.3;
  }
  .text-modal .modal-subtitle {
    margin-bottom: 0.5rem;
    font-size: 0.7rem;
  }
  .modal-font-card {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 0.7rem;
    padding: 2px 1px;
  }
  .modal-font-grid {
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 6px;
  }
}
@media (max-width: 480px) {
  .modal-font-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

.text-thumb-fallback {
  font-size: 0.65rem;
  font-weight: bold;
  text-align: center;
  padding: 2px;
  overflow: hidden;
  max-width: 100%;
}
@media (max-width: 850px) {
  .text-thumb-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 3px;
    max-height: 120px;
  }
}
</style>